// ✅ src/pages/VehicleV2/VehiclewiseDieselIssueReport_Print.jsx
// PRINT page (matches your UI template) + supports querystring + uses your real API response columns.
//
// API:
//   GET http://nissi-india.com/api/dieselConsumption/vehicleWiseIssue
//
// Response sample row keys (you shared):
//   fld_vehiclecode, fld_vehiclename, fld_vehicleregno, fld_branchname,
//   diselissue, workingkm, workingHRS,
//   fld_cummulativekm, fld_cummulativehr,
//   fld_diselconsumptionkm, fld_diselconsumptionhr,
//   DieselConsumption, monthname

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaPrint } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

const API_BASE = "http://nissi-india.com/api/dieselConsumption";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && toStr(v) !== "") return v;
  }
  return fallback;
};

const toNum = (v, d = 0) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};

const fmt2 = (v) => {
  const n = toNum(v, NaN);
  return Number.isFinite(n) ? n.toFixed(2) : toStr(v) || "-";
};

export default function VehiclewiseDieselIssueReport_Print() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const passed = location?.state || {};
  const qs = useMemo(() => Object.fromEntries([...searchParams.entries()]), [searchParams]);
  const src = useMemo(() => ({ ...qs, ...passed }), [qs, passed]);

  // ✅ Crystal/API params
  const filters = useMemo(() => {
    const vehtype = toStr(src?.vehtype ?? src?.vehicleType ?? src?.vehicleMainType ?? src?.vehicletype);

    const branchRaw = toStr(src?.branch ?? src?.vehiclebranch ?? src?.branchId ?? src?.branchName);
    const branch = isSelect(branchRaw) ? "" : branchRaw;

    const month = toStr(src?.month ?? src?.vehiclemonth); // your URL uses month=2
    const year = toStr(src?.year ?? src?.vehicleyear);

    const fromdate = toStr(src?.fromdate ?? src?.fromDate ?? src?.vehcilefromdate);
    const todate = toStr(src?.todate ?? src?.toDate ?? src?.vehciletodate);

    const vehname = toStr(src?.vehname ?? src?.vehiclename ?? src?.vehicleName);
    const vehcode = toStr(src?.vehcode ?? src?.vehiclecode ?? src?.vehicleCode);
    const vehRegno = toStr(src?.vehRegno ?? src?.vehregno ?? src?.vehicleregno ?? src?.logvehicleregno);

    return {
      vehtype,
      branch,
      month,
      year,
      fromdate,
      todate,
      vehname,
      vehRegno,
      vehcode,
      loginuserid: Number(sessionStorage.getItem("loginuserid")) || 0,
      branchType: toStr(sessionStorage.getItem("branchtype") || src?.branchType),
    };
  }, [src]);

  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [rows, setRows] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      setErrMsg("");

      const missingAll =
        !filters.vehtype &&
        !filters.branch &&
        !filters.month &&
        !filters.year &&
        !filters.fromdate &&
        !filters.todate &&
        !filters.vehname &&
        !filters.vehRegno &&
        !filters.vehcode;

      if (missingAll) throw new Error("Print params missing. Open print from Vehiclewise Diesel Issue Report page.");

      const { data } = await axios.get(`${API_BASE}/vehicleWiseIssue`, { params: filters });
      if (!data?.success) throw new Error(data?.message || "Failed to load report");

      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load report";
      setErrMsg(msg);
      setRows([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- REAL field getters (your response) ---------------- */
  const gRegNo = (r) => pick(r, ["fld_vehicleregno", "vehicleregno", "vehRegNo"], "-");
  const gVehCode = (r) => pick(r, ["fld_vehiclecode", "vehiclecode"], "-");
  const gVehName = (r) => pick(r, ["fld_vehiclename", "vehiclename"], "-");
  const gBranch = (r) => pick(r, ["fld_branchname", "branchname"], "-");
  const gMonth = (r) => pick(r, ["monthname", "MonthName"], "-");

  const gDieselIssue = (r) => pick(r, ["diselissue", "dieselissue", "DieselIssue"], "");
  const gWorkKm = (r) => pick(r, ["workingkm", "WorkingKM", "workkm"], "");
  const gWorkHr = (r) => pick(r, ["workingHRS", "workinghrs", "WorkingHRS", "workhrs"], "");

  const gCumKm = (r) => pick(r, ["fld_cummulativekm", "cummulativekm"], "");
  const gCumHr = (r) => pick(r, ["fld_cummulativehr", "cummulativehr"], "");

  const gConsKm = (r) => pick(r, ["fld_diselconsumptionkm", "dieselconsumptionkm"], "");
  const gConsHr = (r) => pick(r, ["fld_diselconsumptionhr", "dieselconsumptionhr"], "");

  const gDieselCons = (r) => pick(r, ["DieselConsumption", "dieselConsumption"], "");

  // totals (computed)
  const totals = useMemo(() => {
    const t = {
      diesel: 0,
      workkm: 0,
      workhr: 0,
      cumkm: 0,
      cumhr: 0,
    };
    rows.forEach((r) => {
      t.diesel += toNum(gDieselIssue(r), 0);
      t.workkm += toNum(gWorkKm(r), 0);
      t.workhr += toNum(gWorkHr(r), 0);
      t.cumkm += toNum(gCumKm(r), 0);
      t.cumhr += toNum(gCumHr(r), 0);
    });
    return t;
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`${isDark ? "bg-gray-900" : "bg-gray-100"} min-h-screen`}>
    
        <div className="w-full bg-white">
          <style>{`
            @page { size: A4 portrait; margin: 10mm; }
            @media print {
              body { background: #fff !important; }
              .no-print { display: none !important; }
              .print-area { margin: 0 !important; padding: 0 !important; }
            }
          `}</style>

          {/* Toolbar */}
          <div className="no-print flex items-center justify-between px-4 py-3 border-b bg-white">
            <div className="text-sm">
              {loading ? "Loading..." : errMsg ? <span className="text-red-600">{errMsg}</span> : `Records: ${rows.length}`}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
              >
                <FaArrowLeft /> Back
              </button>

              <button
                onClick={() => window.print()}
                disabled={loading || !rows.length}
                className={`bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60`}
              >
                <FaPrint /> Print
              </button>
            </div>
          </div>

          {/* Print Area */}
          <div className="print-area mx-auto max-w-[820px] p-4 print:p-0">
            <div className="border-2 border-black p-3">
              <div className="border border-black">
                {/* Header */}
                <div className="border-b border-black p-2">
                  <div className="flex items-center gap-3">
                    <div className="w-[110px] h-[80px] border border-black flex items-center justify-center">
                      <div className="text-[10px] font-bold text-center leading-3">
                        UNDER
                        <br />
                        CONSTRUCTION
                      </div>
                    </div>

                    <div className="flex-1 text-center leading-5">
                      <div className="font-bold text-lg">Nissi Infotech</div>
                      <div className="font-semibold text-sm">Nissi</div>
                      <div className="font-semibold text-sm">CIVIL CONSTRUCTION</div>
                      <div className="text-[11px] mt-1">
                        3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
                      </div>
                      <div className="text-[11px]">
                        Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
                      </div>
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="border-b border-black text-center font-bold py-2 text-sm">
                  VEHICLE WISE DIESEL ISSUE REPORT
                </div>

                {/* Filter info */}
                <div className="border-b border-black p-2 text-[12px]">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div>
                        <span className="font-semibold">Vehicle Type</span> : {filters.vehtype || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Branch</span> : {filters.branch || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Month / Year</span> : {filters.month || "-"} / {filters.year || "-"}
                      </div>
                    </div>

                    <div className="text-right">
                      <div>
                        <span className="font-semibold">From</span> : {filters.fromdate || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">To</span> : {filters.todate || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Reg No</span> : {filters.vehRegno || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Vehicle</span> : {filters.vehcode || "-"} / {filters.vehname || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table (matches your API) */}
                <div className="p-2">
                  <table className="w-full border border-black border-collapse text-[11px]">
                    <thead>
                      <tr>
                        <th className="border border-black px-1 py-1 w-[5%]">S.No</th>
                        <th className="border border-black px-1 py-1 w-[12%]">Reg No</th>
                        <th className="border border-black px-1 py-1 w-[9%]">Veh Code</th>
                        <th className="border border-black px-1 py-1 w-[16%]">Vehicle Name</th>
                        <th className="border border-black px-1 py-1 w-[12%]">Branch</th>
                        <th className="border border-black px-1 py-1 w-[9%]">Month</th>
                        <th className="border border-black px-1 py-1 w-[8%]">Diesel Issue</th>
                        <th className="border border-black px-1 py-1 w-[7%]">Work KM</th>
                        <th className="border border-black px-1 py-1 w-[7%]">Work HRS</th>
                        <th className="border border-black px-1 py-1 w-[7%]">Cum KM</th>
                        <th className="border border-black px-1 py-1 w-[7%]">Cum HRS</th>
                        <th className="border border-black px-1 py-1 w-[7%]">Cons/KM</th>
                        <th className="border border-black px-1 py-1 w-[7%]">Cons/HR</th>
                      </tr>
                    </thead>

                    <tbody>
                      {!loading && rows.length === 0 ? (
                        <tr>
                          <td className="border border-black px-2 py-2 text-center" colSpan={13}>
                            No records found.
                          </td>
                        </tr>
                      ) : (
                        rows.map((r, idx) => (
                          <tr key={pick(r, ["fld_order", "fld_vehicle", "id"], `${idx}`)}>
                            <td className="border border-black px-1 py-1 text-center">{idx + 1}</td>
                            <td className="border border-black px-1 py-1">{gRegNo(r)}</td>
                            <td className="border border-black px-1 py-1">{gVehCode(r)}</td>
                            <td className="border border-black px-1 py-1">{gVehName(r)}</td>
                            <td className="border border-black px-1 py-1">{gBranch(r)}</td>
                            <td className="border border-black px-1 py-1">{gMonth(r) || "-"}</td>

                            <td className="border border-black px-1 py-1 text-right">{fmt2(gDieselIssue(r))}</td>
                            <td className="border border-black px-1 py-1 text-right">{fmt2(gWorkKm(r))}</td>
                            <td className="border border-black px-1 py-1 text-right">{fmt2(gWorkHr(r))}</td>
                            <td className="border border-black px-1 py-1 text-right">{fmt2(gCumKm(r))}</td>
                            <td className="border border-black px-1 py-1 text-right">{fmt2(gCumHr(r))}</td>

                            <td className="border border-black px-1 py-1 text-right">{fmt2(gConsKm(r))}</td>
                            <td className="border border-black px-1 py-1 text-right">{fmt2(gConsHr(r))}</td>
                          </tr>
                        ))
                      )}
                    </tbody>

                    {rows.length > 0 && (
                      <tfoot>
                        <tr>
                          <td className="border border-black px-1 py-1 font-semibold text-right" colSpan={6}>
                            Total
                          </td>
                          <td className="border border-black px-1 py-1 font-semibold text-right">{totals.diesel.toFixed(2)}</td>
                          <td className="border border-black px-1 py-1 font-semibold text-right">{totals.workkm.toFixed(2)}</td>
                          <td className="border border-black px-1 py-1 font-semibold text-right">{totals.workhr.toFixed(2)}</td>
                          <td className="border border-black px-1 py-1 font-semibold text-right">{totals.cumkm.toFixed(2)}</td>
                          <td className="border border-black px-1 py-1 font-semibold text-right">{totals.cumhr.toFixed(2)}</td>
                          <td className="border border-black px-1 py-1" />
                          <td className="border border-black px-1 py-1" />
                        </tr>

                        {/* Optional extra line if you want DieselConsumption overall */}
                        <tr>
                          <td className="border border-black px-1 py-1 font-semibold text-right" colSpan={12}>
                            DieselConsumption (from API field)
                          </td>
                          <td className="border border-black px-1 py-1 font-semibold text-right">
                            {fmt2(pick(rows?.[0], ["DieselConsumption"], ""))}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>

                {/* Footer */}
                <div className="border-t border-black p-2">
                  <div className="border border-black p-2 text-[11px]">
                    Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
                  </div>
                </div>
              </div>
            </div>

            {/* Debug */}
            <div className="no-print mt-4 text-xs text-gray-600">
              <details>
                <summary className="cursor-pointer">Debug: api params + first row</summary>
                <pre className="mt-2 p-3 border rounded bg-gray-50 overflow-auto">
                  {JSON.stringify({ filters, firstRow: rows?.[0] || null }, null, 2)}
                </pre>
              </details>
            </div>
          </div>

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
     
    </div>
  );
}